import React, { useCallback, useContext, useEffect, useRef } from "react";


import { WebSocketContext } from "../websocket/websocket";
import { ClientObjectUpdate, ClientSyncUpdate, LastUpdate, OBJECT_TYPE, ServerObjectMessage, ServerStoredMessage, ServerLimitMessage, ServerReadonlyMessage } from "./eventTypes";
import { DataBaseContext, DatabaseContextData } from "../storage/db.";
import usePendingUpdates from "../pendindUpdates";
import useWheelId from "../hooks/useWheelId";
import { useReadonly } from "../contexts/readonlyContext";

export interface BeforeSaveHandler {
    (update: ClientObjectUpdate): ClientObjectUpdate;
}

export interface DispatcherContextData {
    sendLocalUpdate: (update: ClientObjectUpdate) => void;
    addObjectUpdatesListener: (listener: (update: ClientObjectUpdate) => void, object_type: OBJECT_TYPE, uuid: string) => string
    removeObjectUpdatesListener: (listenerId: string) => void
    addTableUpdatesListener: (listener: (update: ClientObjectUpdate) => void, object_type: OBJECT_TYPE) => string
    removeTableUpdatesListener: (listenerId: string) => void
    addBeforeSaveListener: (handler: BeforeSaveHandler, object_type: OBJECT_TYPE) => string
    removeBeforeSaveListener: (handlerId: string) => void
}


export const DispatcherContext = React.createContext<DispatcherContextData | undefined>(undefined);


interface TableListener {
    object_type: OBJECT_TYPE
    listener: (update: ClientObjectUpdate) => void
}

interface ObjectListener extends TableListener {
    uuid: string
}

interface TableListenerList {
    [key: string]: TableListener;
}

interface ObjectListenerList {
    [key: string]: ObjectListener
}

interface BeforeSaveListenerList {
    [key: string]: {
        handler: BeforeSaveHandler;
        object_type: OBJECT_TYPE;
    };
}

export const getLastUpdateDate = async (db: DatabaseContextData) => {
    const obj = await db.getObject('LASTUPDATE', 'lastupdate');
    if (obj)
        // @ts-ignore
        return (obj as LastUpdate).timestamp || Math.round((obj as LastUpdate).date.getTime() / 1000);
    return null
}

export const DispatcherProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const websocket = useContext(WebSocketContext);
    const wheelId = useWheelId();
    const db = useContext(DataBaseContext);
    const { readonly, setForceReadonly } = useReadonly();
    const tablesListeners = useRef<TableListenerList>({});
    const objectsListeners = useRef<ObjectListenerList>({});
    const beforeSaveListeners = useRef<BeforeSaveListenerList>({});
    const lastUpdateDate = useRef<number | null | undefined>(undefined);
    const pendingObjectCountRef = useRef(0);
    const { storeUpdate, removePendingUpdate, storeSyncUpdate, preparePendingUpdates } = usePendingUpdates();
    const runListeners = useCallback((update: ClientObjectUpdate | ServerObjectMessage) => {
        // console.log(`run ${Object.values(tablesListeners.current).length} table listeners`)
        // console.log(`run ${Object.values(objectsListeners.current).length} objects listeners`)
        Object.values(tablesListeners.current).filter(l => l.object_type === update.object_type).map(listener => {
            listener.listener(update);
        })
        Object.values(objectsListeners.current).filter(l => l.object_type === update.object_type && l.uuid === update.uuid).map(listener => {
            listener.listener(update);
        });
    }, []);

    const sendLocalUpdate = useCallback((update: ClientObjectUpdate) => {
        // Вызываем beforeSave хендлеры
        let processedUpdate = update;
        Object.values(beforeSaveListeners.current)
            .filter(l => l.object_type === update.object_type)
            .forEach(l => {
                processedUpdate = l.handler(processedUpdate);
            });

        (async () => {
            if (processedUpdate.toDelete) {
                await db.deleteObject(processedUpdate.object_type, processedUpdate.uuid);
            } else {
                await db.updateObject(processedUpdate.object_type, processedUpdate.uuid, processedUpdate.data);
            }
            
            // В READONLY режиме не отправляем обновления на сервер и другим вкладкам
            if (readonly) {
                return;
            }
            
            const updateId = await storeUpdate(processedUpdate);
            if (websocket?.connected) {
                websocket.sendMessage(JSON.stringify({ ...processedUpdate, id: updateId }));
            } else {
                websocket?.localChannel?.postMessage({ ...processedUpdate, tabWheelId: wheelId });
            }
        })().then()
        runListeners(processedUpdate);
    }, [db?.db, websocket?.connected, storeUpdate, wheelId, runListeners, readonly]);

    useEffect(() => {
        (async () => {
            if (websocket) {
                if (websocket.connected && !websocket.syncDone) {
                    const msg: ClientSyncUpdate = {
                        type: "SYNC"
                    }
                    if (lastUpdateDate.current === undefined) {
                        lastUpdateDate.current = await getLastUpdateDate(db);
                    }
                    if (lastUpdateDate.current !== null)
                        msg.since = lastUpdateDate.current
                    websocket.sendMessage(JSON.stringify(msg))
                }
            }
        })().then()
    }, [websocket?.connected, websocket?.syncDone]);

    const sendPendingUpdates = useCallback(async () => {
        const pendingUpdates = await preparePendingUpdates();
        if (!websocket?.connected)
            throw new Error('socket not ready');
        // В READONLY режиме не отправляем pending updates
        if (readonly) {
            return;
        }
        pendingUpdates.map(pu => {
            websocket.sendMessage(JSON.stringify({ ...pu.update, id: pu.id }))
        })
    }, [db?.db, websocket?.connected, preparePendingUpdates, readonly]);

    useEffect(() => {
        (async () => {
            if (websocket?.lastJsonMessage) {
                // console.log(websocket.lastJsonMessage)
                if (websocket.lastJsonMessage.type == "SYNC_DONE") {
                    await sendPendingUpdates();
                    websocket.setSyncDone(true);
                }

                if (websocket.lastJsonMessage.type === "LIMIT") {
                    // Сервер сообщил о превышении лимита
                    const limitMsg = websocket.lastJsonMessage as ServerLimitMessage;
                    websocket.addLimitWarning?.(limitMsg.object_type);
                }

                if (websocket.lastJsonMessage.type == "OBJECT") {
                    const message = websocket.lastJsonMessage as ServerObjectMessage;
                    // Увеличиваем счётчик ожидающих объектов
                    websocket.pendingObjectCountRef.current++;

                    if (!!websocket && !websocket.syncDone) {
                        storeSyncUpdate(message);
                    }

                    if (lastUpdateDate.current === undefined) {
                        lastUpdateDate.current = await getLastUpdateDate(db);
                    }
                    try {

                        if (db) {
                            if (message.toDelete) {
                                await db.deleteObject(message.object_type, message.uuid);
                            } else {
                                // console.log('update object from ws', message);
                                await db.updateObject(message.object_type, message.uuid, message.data);
                            }
                            // console.log('current last update', lastUpdateDate.current);
                            if ((lastUpdateDate.current || 0) < message.last_update_timestamp) {
                                // console.log('store last update', { uuid: 'lastupdate', timestamp: message.last_update_timestamp });
                                await db.putObject("LASTUPDATE", { uuid: 'lastupdate', timestamp: message.last_update_timestamp });
                                lastUpdateDate.current = message.last_update_timestamp;
                            }
                        }
                        runListeners(message);
                    } finally {
                        // Уменьшаем счётчик после завершения обработки
                        websocket.pendingObjectCountRef.current--;
                        // Если больше нет объектов — синхронизация завершена
                        if (websocket.pendingObjectCountRef.current === 0) {
                            setTimeout(() => {
                                websocket.setSyncInProgress(false);
                            }, 0);
                        }
                    }

                }
                if (websocket.lastJsonMessage.type === "STORED") {
                    // сигнал подтверждения от сервера. удаляем соотв. ожидающее сообщение.
                    const message = websocket.lastJsonMessage as ServerStoredMessage;
                    await removePendingUpdate(message.id);
                }

                if (websocket.lastJsonMessage.type === "READONLY") {
                    // Сервер принудительно включил режим READONLY
                    setForceReadonly(true);
                }
            }
        })().then()

    }, [websocket?.lastJsonMessage, db?.db, websocket?.syncDone, removePendingUpdate, storeSyncUpdate, setForceReadonly]);

    const addObjectUpdatesListener = useCallback((listener: (update: ClientObjectUpdate) => void, object_type: OBJECT_TYPE, uuid: string) => {
        const listenerUuid = crypto.randomUUID();
        objectsListeners.current[listenerUuid] = {
            listener,
            object_type,
            uuid,
        }
        return listenerUuid;

    }, []);

    const addTableUpdatesListener = useCallback((listener: (update: ClientObjectUpdate) => void, object_type: OBJECT_TYPE) => {
        const listenerUuid = crypto.randomUUID();
        tablesListeners.current[listenerUuid] = {
            listener,
            object_type,
        }
        return listenerUuid;

    }, []);

    const addBeforeSaveListener = useCallback((handler: BeforeSaveHandler, object_type: OBJECT_TYPE) => {
        const handlerUuid = crypto.randomUUID();
        beforeSaveListeners.current[handlerUuid] = {
            handler,
            object_type,
        }
        return handlerUuid;

    }, []);

    const removeObjectUpdatesListener = useCallback((listenerId: string) => {
        delete objectsListeners.current[listenerId];
    }, []);

    const removeTableUpdatesListener = useCallback((listenerId: string) => {
        delete tablesListeners.current[listenerId];
    }, []);

    const removeBeforeSaveListener = useCallback((handlerId: string) => {
        delete beforeSaveListeners.current[handlerId];
    }, []);


    useEffect(() => {
        if (websocket?.localChannel) {
            websocket.localChannel.addEventListener('message', event => {
                if (wheelId === event.data.tabWheelId) {
                    const data = { ...event.data };
                    delete data.tabWheelId;
                    runListeners(data);
                }

            })
        }
    }, [websocket?.localChannel, runListeners, wheelId])
    return <DispatcherContext.Provider value={{
        sendLocalUpdate,
        addObjectUpdatesListener,
        addTableUpdatesListener,
        addBeforeSaveListener,
        removeObjectUpdatesListener,
        removeTableUpdatesListener,
        removeBeforeSaveListener
    }}>{children}</DispatcherContext.Provider>

}